import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EXAM_REGISTRATION } from '../../shared/Routes';
import { ApplicationApprovedCard } from './ApplicationApprovedCard';
import { ApplicationInReviewCard } from './ApplicationInReviewCard';
import { BatchClosedCard } from './BatchClosedCard';
import { ExamFailedCard } from './ExamFailedCard';
import { ExamPassedCard } from './ExamPassedCard';
import { InvalidBatchCard } from './InvalidBatchCard';
import { ReApplyAfterSometimeCard } from './ReApplyAfterSometimeCard';
import { useApplicantHome } from './useApplicantHome';
import useQueryParams from '../../hooks/useQueryParams';
import { NewApplicationWizardContainer } from './NewApplicationWizard';
import { NotApprovedCard } from './NotApprovedCard';
import { SelfRegistrationRole } from '../functionary/SelfRegistration/SelfRegistrationRole';

export const ApplicantHome = () => {
	const navigate = useNavigate();
	const queryParams = useQueryParams();

	const { messageToShow, showNewApplicationWizard } = useApplicantHome();

	const getRouteWithParams = useCallback(
		(route: string) => {
			return `${route}?${queryParams.toString()}`;
		},
		[queryParams]
	);

	useEffect(() => {
		if (typeof messageToShow === 'object') {
			const examPhase =
				messageToShow?.candidateModule?.candidateExamPhaseDetail;
			const module = messageToShow.candidateModule?.module;

			if (
				module === 'EXAM_MODULE' &&
				(!examPhase?.registeredForExam ||
					(examPhase.registeredForExam &&
						!examPhase.candidateExaminationDetail.examResultStatus))
			) {
				navigate(getRouteWithParams(EXAM_REGISTRATION), {
					state: {
						candidateExamPhaseDetail: examPhase,
					},
				});
			}
		}
	}, [messageToShow, navigate, getRouteWithParams]);

	switch (messageToShow) {
		case 'BATCH_CLOSED':
		case 'BATCH_NOT_STARTED_YET':
			return <BatchClosedCard />;

		case 'INVALID_BATCH':
			return <InvalidBatchCard />;

		case 'PAST_APPLICATION_UNDER_REVIEW':
			return <ApplicationInReviewCard />;

		case 'ALREADY_A_PRECEPTOR':
			return <SelfRegistrationRole />;

		case 'BLACKLISTED':
			return <NotApprovedCard />;
	}

	const examDetail =
		messageToShow?.candidateModule?.candidateExamPhaseDetail
			.candidateExaminationDetail;

	if (examDetail?.examResultStatus === 'CLEARED') {
		return <ExamPassedCard candidateExamDetail={examDetail} />;
	} else if (examDetail?.examResultStatus === 'NOT_CLEARED') {
		return <ExamFailedCard candidateExamDetail={examDetail} />;
	}

	if (messageToShow?.pastApplicationId) {
		return (
			<ApplicationApprovedCard
				allowed={messageToShow?.allowed}
				pastApplicationId={messageToShow.pastApplicationId}
				preworkCompletionStatus={messageToShow.preworkCompletionStatus}
				preworkMentorCompletionStatus={
					messageToShow.preworkMentorCompletionStatus
				}
				currentBatch={messageToShow.currentBatch}
				approverPreworkRecommendation={
					messageToShow.approverPreworkRecommendation
				}
				isPreworkStautsRejectedByApprover={
					messageToShow.isPreworkStautsRejectedByApprover
				}
				preworkStatusFunctionaryPredefinedComments={
					messageToShow.preworkStatusFunctionaryPredefinedComments
				}
				reapplyDateForRejectedPrework={
					messageToShow.reapplyDateForRejectedPrework
				}
				pastApplicationBatch={messageToShow.pastApplicationBatch}
			/>
		);
	}

	if (messageToShow?.pastApplicationRejectedReasons) {
		return (
			<ReApplyAfterSometimeCard
				allowed={messageToShow?.allowed}
				reApplyDate={messageToShow?.reapplyDate}
				pastApplicationRejectedReasons={
					messageToShow.pastApplicationRejectedReasons
				}
				pastApplicationBatch={messageToShow.pastApplicationBatch}
				currentBatch={messageToShow.currentBatch}
			/>
		);
	}

	return <>{showNewApplicationWizard && <NewApplicationWizardContainer />}</>;
};
